import {
  RELATION__ACCOUNTS_STARRED,
  RELATION__CANDIDATES_STARRED,
  RELATION__EMPLOYERS_STARRED,
  RELATION__ENGAGEMENTS_STARRED,
  RELATION__JOBS_STARRED
} from '../../dictionaries/Relations.dic';
import AccountLib from '../Account';
import {
  Arr
} from '../Array.lib';
import Candidate from '../Candidate';
import Core from '../Core';
import Employer from '../Employer';
import Engagement from '../Engagement';
import globalErrorHandler from '../Error/globalErrorHandler.fun';
import Http from '../Http';
import Job from '../Job';
import {
  Obj
} from '../Object.lib';
import {
  COLLECTION__ACCOUNTS,
  COLLECTION__ACCOUNTS_STARRED,
  COLLECTION__CANDIDATES,
  COLLECTION__CANDIDATES_STARRED,
  COLLECTION__EMPLOYERS,
  COLLECTION__EMPLOYERS_STARRED,
  COLLECTION__ENGAGEMENTS,
  COLLECTION__ENGAGEMENTS_STARRED,
  COLLECTION__JOBS,
  COLLECTION__JOBS_STARRED,
  readLoopbackRecord
} from '../services/BE/loopback.api';


/**
 * Generic function to update starred for any entity.
 *
 * @param {Object} options - object with following properties:
 * @param {string} options.entityId - id of the entity being starred
 * @param {string} options.collection - name of the collection from which the entity is being starred
 * @param {boolean} options.starred - whether to set the starred flag to true or false
 *
 * @returns {Promise<Object>} - Promise that resolves with the API response
 */
export default async function updateStarred({
  entityId = '',
  collection = '',
  starred = false
}) {
  try {
    if (!entityId || !collection || typeof starred !== 'boolean') {
      throw new Error('Invalid parameters in updateStarred');
    }
    /**
     * @type {Object} entityMap
     * @description Map of options for each collection with following properties:
     * {
     *   starredEntityField: 'accountId', // ENTITY to be starred
     *   starredAccountField: 'userId', // ACCOUNT doing the starring (note only accounts can star other accounts, for that the userId is used instead of accountId)
     *   relationField: 'accountsStarred', // STARRED RELATION
     *   relationCollection: 'AccountStarred', // STARRED COLLECTION
     * }
     */
    const entityMap = Obj(
      {
        [COLLECTION__ACCOUNTS]: {
          starredEntityField: 'accountId',
          starredAccountField: 'userId',
          relationField: RELATION__ACCOUNTS_STARRED,
          relationCollection: COLLECTION__ACCOUNTS_STARRED,
          cacheCleaner: AccountLib.cleanCache,
        },
        [COLLECTION__CANDIDATES]: {
          starredEntityField: 'candidateId',
          starredAccountField: 'accountId',
          relationField: RELATION__CANDIDATES_STARRED,
          relationCollection: COLLECTION__CANDIDATES_STARRED,
          cacheCleaner: Candidate.cleanCache,
        },
        [COLLECTION__EMPLOYERS]: {
          starredEntityField: 'employerId',
          starredAccountField: 'accountId',
          relationField: RELATION__EMPLOYERS_STARRED,
          relationCollection: COLLECTION__EMPLOYERS_STARRED,
          cacheCleaner: Employer.cleanCache
        },
        [COLLECTION__ENGAGEMENTS]: {
          starredEntityField: 'engagementId',
          starredAccountField: 'accountId',
          relationField: RELATION__ENGAGEMENTS_STARRED,
          relationCollection: COLLECTION__ENGAGEMENTS_STARRED,
          cacheCleaner: Engagement.cleanCache
        },
        [COLLECTION__JOBS]: {
          starredEntityField: 'jobId',
          starredAccountField: 'accountId',
          relationField: RELATION__JOBS_STARRED,
          relationCollection: COLLECTION__JOBS_STARRED,
          cacheCleaner: Job.cleanCache
        },
      }[collection]
    );
    const entity = Obj(
      await readLoopbackRecord({
        collection,
        where: { id: entityId },
        fields: ['id'],
        limit: 1,
        include: {
          relation: entityMap.relationField,
          scope: {
            where: {
              [entityMap.starredEntityField]: entityId,
              [entityMap.starredAccountField]: Core.getUserId()
            }
          },
        }
      })
    );
    const existingRelationRecords = Arr(entity[entityMap.relationField]);

    // CLEAN CACHE
    entityMap.cacheCleaner();

    // DELETE all existing records for this entity and account before creating a new one
    for (const existingRelationRecord of existingRelationRecords) {
      await Http.delete(
        Core.getApi(`${entityMap.relationCollection}/${existingRelationRecord.id}`)
      );
    }

    // CREATE a new record for this entity and account if starred
    const result = {};
    if (starred === true) {
      Object.assign(result,
        await Http.post(Core.getApi(entityMap.relationCollection), {
          [entityMap.starredEntityField]: entityId,
          [entityMap.starredAccountField]: Core.getUserId(),
          starred,
        })
      );
    }

    return result;
  }
  catch (error) {
    globalErrorHandler(error);
  }
}